import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { reviewApi } from '../../utils/reviewApi';
import { FileChip, ProgressBar, RoutingPill } from './reviewUi';

const PAGE = 25;

const WHY_BLOCKED = {
  not_draft: 'A review can start only while the document is in Draft',
  no_permission: 'Only users with permission to start routing this document can start a review',
  no_key: 'This row has no document key — add Key to the filter',
  active_review: 'This document already has an open review — complete or cancel it first',
};

/** The review column: none / "Round 2" / "2 reviews · 1 open" / complete. */
function ReviewCell({ review }) {
  if (!review || review.status === 'none') {
    return <span className="drv-pill drv-pill--none">{review?.any_revision ? 'Not on this revision' : 'Not started'}</span>;
  }
  if (review.status === 'complete') {
    return <span className="drv-pill drv-pill--done">{review.reviews > 1 ? `${review.reviews} reviews · ` : ''}Reviewed</span>;
  }
  const text = review.reviews > 1
    ? `${review.reviews} reviews · ${review.open} open`
    : `Round ${review.current_round || 1}`;
  return <span className="drv-pill drv-pill--open">{text}</span>;
}

function ProgressCell({ review }) {
  const p = review?.progress;
  if (!p || !p.reviewers_total) return <span className="drv-muted">—</span>;
  const label = `${p.reviewers_done} of ${p.reviewers_total} reviewers done · ${p.comments_open} open comment${p.comments_open === 1 ? '' : 's'}`;
  return (
    <>
      <ProgressBar done={p.reviewers_done} total={p.reviewers_total} label={label} />
      <div className="drv-small" style={{ fontSize: 11, marginTop: 4 }}>{label}</div>
    </>
  );
}

/**
 * One Doc Review panel (mockup screen 1): the DMS items its filter returns, inline — there is
 * no Full View (D2). Search and routing state filter on the server, across every row.
 */
export default function ReviewPanelCard({ panel, canManage, onEdit, onDelete, onStart, reloadKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [loading, setLoading] = useState(false);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Responses can arrive out of order (an early "2" slower than the later "26291"), so only
  // the newest request may write state — otherwise a stale result overwrites the current one.
  const seq = useRef(0);
  const load = useCallback(async (refresh = false) => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const d = await reviewApi.rows(panel.id, { page, limit: PAGE, search: query, state, refresh: refresh ? 1 : '' });
      if (mine === seq.current) setData(d);
    } catch (e) {
      if (mine === seq.current) setError(e.message);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [panel.id, page, query, state]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <section className="drv-panel" aria-label={panel.name}>
      <div className="drv-panel-head">
        <div className="drv-panel-title">
          <div>
            <h2 className="drv-panel-name">{panel.name}</h2>
            <div className="drv-panel-meta">
              Filter: {panel.filter_name || `#${panel.filter_id}`}
              {data && ` · ${data.all_total} document${data.all_total === 1 ? '' : 's'} · ${data.drafts} draft${data.drafts === 1 ? '' : 's'}`}
              {!panel.is_public && ' · only you'}
            </div>
          </div>
          <span className="drv-info">
            <button type="button" className="drv-info-btn" aria-label="About this list">i</button>
            <span className="drv-info-pop" role="tooltip">
              All documents in the filter are listed. A review can start only while the document’s routing
              state is <strong>Draft</strong>, and only by users with permission to start routing the document.
              Routing itself stays in Orcanos.
            </span>
          </span>
        </div>
        <div className="drv-panel-tools">
          <label>Routing state
            <select className="drv-select" value={state} onChange={(e) => { setState(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {(data?.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <input type="search" className="drv-input" aria-label={`Search in ${panel.name}`} placeholder="Search"
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 160 }} />
          <button type="button" className="drv-iconbtn" title="Refresh from Orcanos" aria-label="Refresh from Orcanos"
            onClick={() => load(true)} disabled={loading}>↻</button>
          {canManage && panel.can_edit && (
            <>
              <button type="button" className="drv-iconbtn" title="Edit panel" aria-label="Edit panel" onClick={onEdit}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" /></svg>
              </button>
              <button type="button" className="drv-iconbtn" title="Delete panel" aria-label="Delete panel" onClick={onDelete}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="drv-error" style={{ margin: 16 }} role="alert">{error}</div>}
      {data?.missing_columns?.length > 0 && (
        <div className="drv-note" style={{ margin: 16 }}>
          This panel’s filter no longer selects: <strong>{data.missing_columns.join(', ')}</strong>.
          Add {data.missing_columns.length === 1 ? 'it' : 'them'} to the filter in Orcanos —
          without Routing state no row can start a review.
        </div>
      )}

      <div className="drv-scroll">
        <table className="drv-table">
          <thead>
            <tr>
              <th>Key / Name</th><th>File name</th><th>Revision</th><th>Last approved</th>
              <th>Routing state</th><th>Review</th><th>Progress</th><th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && (
              <tr><td colSpan={8} className="drv-muted">Loading documents from Orcanos…</td></tr>
            )}
            {data && data.rows.length === 0 && (
              <tr><td colSpan={8} className="drv-muted">
                {query || state ? 'No documents match.' : 'This filter returns no documents.'}
              </td></tr>
            )}
            {data?.rows.map((r) => (
              <tr key={r.item_id || r.key}>
                <td>
                  {r.url
                    ? <a className="drv-key" href={r.url} target="_blank" rel="noopener noreferrer" title="Open in Orcanos">{r.key}</a>
                    : <span className="drv-key">{r.key}</span>}
                  <div style={{ marginTop: 2 }}>{r.name}</div>
                </td>
                <td><FileChip fileName={r.file_name} kind={r.file_kind} /><span className="drv-file">{r.file_name}</span></td>
                <td style={{ fontWeight: 600 }}>{r.revision || '—'}</td>
                <td className="drv-muted">{r.last_approved || '—'}</td>
                <td><RoutingPill state={r.routing_state} /></td>
                <td><ReviewCell review={r.review} /></td>
                <td><ProgressCell review={r.review} /></td>
                <td style={{ textAlign: 'right' }}>
                  <div className="drv-actions">
                    <button type="button" className={`drv-btn${r.can_start ? ' drv-btn--primary' : ''}`}
                      disabled={!r.can_start} title={r.can_start ? 'Start a review of this draft' : WHY_BLOCKED[r.start_blocked]}
                      onClick={() => onStart(r)}>Start review</button>
                    {r.review?.any_revision > 0 && (
                      <Link className="drv-btn" to={`/review/item/${encodeURIComponent(r.item_id)}?setup=${panel.id}`}>Show reviews</Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE && (
        <div className="drv-pager">
          <span>{(page - 1) * PAGE + 1}–{Math.min(page * PAGE, data.total)} of {data.total}</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="drv-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Prev</button>
            <button type="button" className="drv-btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ›</button>
          </span>
        </div>
      )}
    </section>
  );
}
